import React from 'react';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const RepeatWindowSettings = ({ settings, onChange }) => {
  const repeatWindowOptions = [
    { value: '1', label: '1 month' },
    { value: '2', label: '2 months' },
    { value: '3', label: '3 months' },
    { value: '6', label: '6 months' },
    { value: '12', label: '1 year' },
    { value: 'never', label: 'Never repeat dishes' }
  ];

  const reminderOptions = [
    { value: '7', label: '1 week before' },
    { value: '3', label: '3 days before' },
    { value: '2', label: '2 days before' },
    { value: '1', label: '1 day before' },
    { value: '0', label: 'Day of event' }
  ];

  const handleSettingChange = (key, value) => {
    onChange({ ...settings, [key]: value });
  };

  const handleReminderToggle = (reminderValue) => {
    const currentReminders = settings?.reminders || [];
    const updatedReminders = currentReminders?.includes(reminderValue)
      ? currentReminders?.filter(r => r !== reminderValue)
      : [...currentReminders, reminderValue];
    
    handleSettingChange('reminders', updatedReminders);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <h3 className="text-lg font-semibold text-foreground mb-4">Advanced Settings</h3>
      <div className="space-y-6">
        {/* Repeat Window Settings */}
        <div>
          <div className="flex items-center space-x-2 mb-3">
            <Icon name="RotateCcw" size={16} className="text-primary" />
            <h4 className="font-medium text-foreground">Dish Repeat Prevention</h4>
          </div>
          
          <Select
            label="Don't repeat dishes within"
            description="Prevent serving the same dishes to guests within this timeframe"
            options={repeatWindowOptions}
            value={settings?.repeatWindow || '3'}
            onChange={(value) => handleSettingChange('repeatWindow', value)}
          />
          
          <div className="mt-3 space-y-2">
            <Checkbox
              label="Apply to all guests globally"
              description="Use the same repeat window for all guests, not per-guest tracking"
              checked={settings?.globalRepeatWindow || false}
              onChange={(e) => handleSettingChange('globalRepeatWindow', e?.target?.checked)}
            />
            
            <Checkbox
              label="Include similar dishes"
              description="Also prevent similar dishes (e.g., different pasta dishes)"
              checked={settings?.includeSimilarDishes || false}
              onChange={(e) => handleSettingChange('includeSimilarDishes', e?.target?.checked)}
            />
          </div>
        </div>

        {/* Reminder Settings */}
        <div>
          <div className="flex items-center space-x-2 mb-3">
            <Icon name="Bell" size={16} className="text-primary" />
            <h4 className="font-medium text-foreground">Event Reminders</h4>
          </div>
          
          <p className="text-sm text-muted-foreground mb-3">
            Choose when you'd like to receive reminders about your dinner event
          </p>
          
          <div className="space-y-2">
            {reminderOptions?.map(option => (
              <Checkbox
                key={option?.value}
                label={option?.label}
                checked={(settings?.reminders || [])?.includes(option?.value)}
                onChange={() => handleReminderToggle(option?.value)}
              />
            ))}
          </div>
        </div>

        {/* Menu Planning Preferences */}
        <div>
          <div className="flex items-center space-x-2 mb-3">
            <Icon name="ChefHat" size={16} className="text-primary" />
            <h4 className="font-medium text-foreground">Menu Planning</h4>
          </div>
          
          <div className="space-y-3">
            <Checkbox
              label="Enable AI menu suggestions"
              description="Get AI-powered menu recommendations based on guest preferences"
              checked={settings?.enableAISuggestions !== false}
              onChange={(e) => handleSettingChange('enableAISuggestions', e?.target?.checked)}
            />
            
            <Checkbox
              label="Auto-generate shopping list"
              description="Automatically create shopping lists from planned menus"
              checked={settings?.autoShoppingList || false}
              onChange={(e) => handleSettingChange('autoShoppingList', e?.target?.checked)}
            />
            
            <Checkbox
              label="Consider seasonal ingredients"
              description="Prioritize seasonal and locally available ingredients"
              checked={settings?.considerSeasonal || false}
              onChange={(e) => handleSettingChange('considerSeasonal', e?.target?.checked)}
            />
            
            <Checkbox
              label="Budget-conscious suggestions"
              description="Factor in cost-effectiveness when suggesting menus"
              checked={settings?.budgetConscious || false}
              onChange={(e) => handleSettingChange('budgetConscious', e?.target?.checked)}
            />
          </div>
        </div>

        {/* Privacy Settings */}
        <div>
          <div className="flex items-center space-x-2 mb-3">
            <Icon name="Shield" size={16} className="text-primary" />
            <h4 className="font-medium text-foreground">Privacy & Sharing</h4>
          </div>
          
          <div className="space-y-3">
            <Checkbox
              label="Allow guests to see menu in advance"
              description="Share planned menu with guests before the event"
              checked={settings?.shareMenuAdvance || false}
              onChange={(e) => handleSettingChange('shareMenuAdvance', e?.target?.checked)}
            />
            
            <Checkbox
              label="Collect post-dinner feedback"
              description="Send feedback forms to guests after the event"
              checked={settings?.collectFeedback || false}
              onChange={(e) => handleSettingChange('collectFeedback', e?.target?.checked)}
            />
            
            <Checkbox
              label="Save event photos"
              description="Allow guests to upload and share photos from the dinner"
              checked={settings?.saveEventPhotos || false}
              onChange={(e) => handleSettingChange('saveEventPhotos', e?.target?.checked)}
            />
          </div>
        </div>

        {/* Summary */}
        <div className="border-t border-border pt-4">
          <div className="p-3 bg-muted rounded-lg">
            <h5 className="font-medium text-foreground mb-2">Settings Summary</h5>
            <div className="text-sm text-muted-foreground space-y-1">
              <p>• Dishes won't repeat for {settings?.repeatWindow === 'never' ? 'any guest ever' : `${settings?.repeatWindow} month${settings?.repeatWindow !== '1' ? 's' : ''}`}</p>
              <p>• {(settings?.reminders || [])?.length} reminder{(settings?.reminders || [])?.length !== 1 ? 's' : ''} configured</p>
              <p>• AI suggestions: {settings?.enableAISuggestions !== false ? 'Enabled' : 'Disabled'}</p>
              <p>• Auto shopping list: {settings?.autoShoppingList ? 'Enabled' : 'Disabled'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RepeatWindowSettings;